import React, { Fragment, useEffect, useRef, useState } from "react";

import PropTypes from "prop-types";

import { ADDRESS, ADDRESS_FIELDS } from "constants/address";
import { DEFAULT_LOCATOR_STRATEGY } from "constants/locatorStrategy";
import { COUNTRY_CODES, MARKET_CENTER } from "constants/market";
import { SERVICE_METHOD, SERVICE_METHOD_SORT } from "constants/order";
import isDelivery from "modules/isDelivery";

import "components/StoreLocator/Market/ES.css";
import GoogleMapsTypeAhead from "components/StoreLocator/Strategy/GoogleMapsTypeAhead";
import Input from "components/StoreLocator/Strategy/Input";
import PinDrop, { MAP_OPTIONS } from "components/StoreLocator/Strategy/PinDrop";

import DominosTypeAhead from "../Strategy/DominosTypeAhead";
import TypeAhead from "../Strategy/TypeAhead";

const placesMethods = Object.freeze([
  SERVICE_METHOD.CARRYOUT,
  SERVICE_METHOD.DELIVERY,
]);

const MARKET_CODE = COUNTRY_CODES.SPAIN;
const MARKET_ZOOM = 6;

const SHOW_ADDRESS_FIELDS = Object.freeze({
  fields: [
    ADDRESS_FIELDS[ADDRESS.DOOR],
    ADDRESS_FIELDS[ADDRESS.STAIRS],
    ADDRESS_FIELDS[ADDRESS.FLOOR],
    ADDRESS_FIELDS[ADDRESS.APARTMENT],
  ],
});

const countryCodeList = window.ENVCONFIG.includeUSMarketForTestStore
  ? [MARKET_CODE, COUNTRY_CODES.UNITED_STATES]
  : [MARKET_CODE];

const ES = (props) => {
  const {
    address,
    // addressLine2,
    city,
    cities,
    getCitiesWithTypeahead,
    getRegions,
    getSearchCep,
    getStreets,
    handleChange,
    latLngAvailability,
    locatorStrategy,
    maps,
    postalCode,
    regions,
    regionCode,
    searchCep,
    serviceMethod,
    setUsesPlaces,
    setLatLngAvailability,
    setShowForm,
    showForm,
    streets,
    streetName,
    streetNumber,
    t,
  } = props;

  const [locState, setLocState] = useState("");
  const [optionalFieldState, setOptionalFieldState] = useState({
    unitNumber: "",
    stairs: "",
    floor: "",
    apartment: "",
  });

  const map = useRef();
  const geocoderService = new maps.Geocoder();
  const geocoderOK = maps.GeocoderStatus.OK;

  const isDefaultLocatorStrategy = locatorStrategy === DEFAULT_LOCATOR_STRATEGY;

  useEffect(() => {
    placesMethods.includes(serviceMethod) && setUsesPlaces(true);
    getRegions(MARKET_CODE);
  }, []);

  useEffect(() => {
    if (address.length || (postalCode && postalCode !== "")) setShowForm(true);
    else setShowForm(false);
  }, [address, postalCode]);

  useEffect(() => {
    if (postalCode && postalCode !== "") {
      const searchCepAddress =
        (address &&
          searchCep.find(({ street }) =>
            new RegExp(street, "i").test(address)
          )) ||
        searchCep[0] ||
        {};
      setLocState(searchCepAddress?.state || "");

      handleChange({
        regionCode:
          Object.entries(regions)
            .find(
              (pair) =>
                pair[1].trim().toLowerCase() ===
                searchCepAddress?.state?.trim().toLowerCase()
            )
            ?.shift() || "",
        city: searchCepAddress?.city || "",
        streetName: searchCepAddress?.street || "",
        neighborhood: "N/A",
      });
    }
  }, [searchCep, postalCode, regions]);

  useEffect(() => {
    if (postalCode && postalCode !== "") {
      setShowForm(true);
    } else {
      setShowForm(false);
    }
  }, [postalCode]);

  useEffect(() => {
    if (!latLngAvailability && isDelivery(serviceMethod)) {
      setUsesPlaces(false);
    }
  }, [latLngAvailability]);

  const transformAddress = (address) => {
    const newAddress = {};
    address.forEach((component) => {
      const { types, long_name, short_name } = component;
      types.forEach((type) => {
        if (!newAddress[type]) {
          newAddress[type] = {};
        }
        newAddress[type] = {
          long_name,
          short_name,
        };
      });
    });

    return newAddress;
  };

  const getGeocodedResult = (request, callback) => {
    geocoderService.geocode(request, (response, status) => {
      if (status !== geocoderOK) {
        props.onError(status);
        return;
      }

      const { address_components, geometry, place_id } = response[0];

      const formattedAddress = transformAddress(address_components);
      const { locality, postal_code, route, street_number } = formattedAddress;

      const postalCode = postal_code?.long_name || "";
      const streetName = route?.long_name || "";
      const streetNumber = street_number?.long_name || "";
      const address = `${streetName}, ${streetNumber}, ${
        locality?.long_name || ""
      }`;

      handleSearchCep(postalCode);

      callback({
        address,
        addressComponents: address_components,
        geometry,
        placeId: place_id,
        postalCode,
        streetName,
        streetNumber,
      });
    });
  };

  const handlePinDrop = (latLng) => {
    getGeocodedResult(
      { location: latLng },
      ({
        address,
        placeId,
        geometry,
        postalCode,
        streetName,
        streetNumber,
      }) => {
        const updateObject = Object.assign(
          {},
          ...Object.keys(ADDRESS_FIELDS)
            .filter((field) => field !== ADDRESS.ADDRESS_TYPE)
            .map((field) => ({ [field]: "" })),
          {
            latitude: geometry.location.lat(),
            longitude: geometry.location.lng(),
          },
          {
            placeId,
          },
          {
            address,
            postalCode,
            streetName,
            streetNumber,
          }
        );

        setLatLngAvailability(true);
        handleChange(updateObject);
      }
    );
  };

  const handlePlaceChange = ({ address: addressValue, placeId = "" }) => {
    handleChange({
      address: addressValue,
      placeId,
    });

    if (placeId) {
      getGeocodedResult({ placeId }, ({ geometry }) => {
        map.current.dropPin(geometry.location);
      });
    }
  };

  const handleCityChange = ({ city }) => {
    setLatLngAvailability(false);

    handleChange({
      city,
    });
  };

  const handleClear = () => {
    getSearchCep({
      cep: "",
      countryCode: MARKET_CODE,
    });
    const updates = {
      state: "",
      city: "",
      streetName: "",
      streetNumber: "",
      address: "",
      placeId: "",
    };

    props.handleChange(updates);
  };

  const getFormattedAddress = ({
    streetNumber,
    city,
    streetName,
    regionCode,
    postalCode,
  }) => {
    const formattedAddress = [
      streetNumber,
      city,
      streetName,
      regionCode,
      postalCode,
    ].reduce((formattedAddress, field) => {
      if (!field) return formattedAddress;
      return formattedAddress ? `${formattedAddress}, ${field}` : field;
    }, "");

    return streetNumber
      ? `${streetNumber} ${formattedAddress}`
      : formattedAddress;
  };

  const handleSearch = () => {
    const address = getFormattedAddress({
      regionCode,
      city,
      streetName,
      streetNumber,
      postalCode,
    });

    getGeocodedResult({ address }, ({ geometry }) => {
      map.current.dropPin(geometry.location);
    });
  };

  const handleSearchCep = (postalCode) => {
    if (postalCode && postalCode !== "") {
      getSearchCep({
        cep: postalCode,
        countryCode: MARKET_CODE,
      });
    }
  };

  const updateStreet = ({ id: streetNameValue }) => {
    setLatLngAvailability(false);

    handleChange({
      streetName: streetNameValue,
    });
  };

  const suggestedStreets = streets
    .map(({ id, ...streetComponents }) => ({
      ...streetComponents,
      id,
      description: id,
    }))
    .slice(0, 4);

  const handleChangeOptionals = (update) => {
    setOptionalFieldState((prev) => ({
      ...optionalFieldState,
      ...prev,
      ...update,
    }));
  };

  useEffect(() => {
    const {
      unitNumber = "",
      stairs = "",
      floor = "",
      apartment = "",
    } = optionalFieldState ?? {};

    const addressParts = [
      unitNumber && `POR-${unitNumber}`,
      stairs && `E-${stairs}`,
      floor && `P-${floor}`,
      apartment && `PTA-${apartment}`,
    ].filter(Boolean);

    const addressLine2 = addressParts.join(", ");
    handleChange({ addressLine2 });
  }, [optionalFieldState]);

  switch (serviceMethod) {
    case SERVICE_METHOD.DELIVERY:
      return (
        <div className="grid">
          {isDefaultLocatorStrategy && (
            <div className="grid__cell--1 grid__cell--1/2@desktop margin__bottom--1_25rem">
              <GoogleMapsTypeAhead
                className="grid__cell--1"
                input={address}
                label={t(ADDRESS_FIELDS[ADDRESS.ADDRESS].label)}
                maps={maps}
                name="address"
                onChange={handlePlaceChange}
                options={{
                  componentRestrictions: {
                    country: countryCodeList,
                  },
                  types: ["geocode"],
                }}
                quidBase="store-locator-address"
              />

              <PinDrop
                mapOptions={Object.assign({}, MAP_OPTIONS, {
                  mapTypeControl: true,
                })}
                mapCenter={MARKET_CENTER.SPAIN}
                mapZoom={MARKET_ZOOM}
                maps={maps}
                onChange={handlePinDrop}
                ref={map}
              />
            </div>
          )}

          {showForm && searchCep.length > 0 && (
            <div className="grid__cell--1 grid__cell--1/2@desktop">
              <div className="grid">
                {isDefaultLocatorStrategy && (
                  <Fragment>
                    <Input
                      className="grid__cell--1 grid__cell--1/2@desktop"
                      label={t(ADDRESS_FIELDS[ADDRESS.STATE].label)}
                      name="state"
                      quidBase="state-address-field"
                      value={locState}
                      handleChange={handleChange}
                      t={t}
                      isRequired={true}
                      disabled={true}
                    />

                    <TypeAhead
                      className="grid__cell--1 grid__cell--1/2@desktop"
                      clearSuggestions={() => {}}
                      input={city}
                      label={t("customer:locations.address.city")}
                      fetchPredictions={() =>
                        getCitiesWithTypeahead({
                          countryCode: COUNTRY_CODES.SPAIN,
                          State: locState,
                        })
                      }
                      quidBase="city-address"
                      onChange={handleCityChange}
                      handleSelect={({ description }) => {
                        setLatLngAvailability(false);
                        handleChange({ city: description });
                      }}
                      name={"city"}
                      suggestions={(cities?.Cities ?? []).map((place) => ({
                        description: place,
                      }))}
                      limitResults={5}
                      required
                    />

                    <DominosTypeAhead
                      className="grid__cell--1"
                      debounce={250}
                      onChange={({ streetName }) => {
                        setLatLngAvailability(false);
                        handleChange({
                          streetName,
                        });
                      }}
                      onSelect={updateStreet}
                      fetchPredictions={(streetNameValue) => {
                        getStreets({
                          countryCode: MARKET_CODE,

                          customHref:
                            "/store-locator-typeahead-service/search/address",
                          street: streetNameValue,
                          streetLimit: "5",
                          power: true,
                        });
                      }}
                      removeDuplicatePredictions={true}
                      label={t(ADDRESS_FIELDS[ADDRESS.STREET_NAME].label)}
                      removePredictionOnBodyClick={true}
                      name="streetName"
                      quidBase="store-locator-address-field"
                      input={streetName}
                      suggestions={suggestedStreets}
                      t={t}
                      required={true}
                    />

                    <Input
                      className="grid__cell--1 grid__cell--1/2@desktop"
                      label={t(ADDRESS_FIELDS[ADDRESS.STREET_NUMBER].label)}
                      name="streetNumber"
                      quidBase="street-number-address-field"
                      value={streetNumber}
                      handleChange={handleChange}
                      t={t}
                      isRequired={true}
                    />

                    {SHOW_ADDRESS_FIELDS.fields.map(
                      ({ label, mapping: key }) => (
                        <Input
                          className="grid__cell--1 grid__cell--1/2@desktop"
                          handleChange={handleChangeOptionals}
                          key={key}
                          label={t(label)}
                          name={key}
                          quidBase="store-locator-address-field"
                          t={t}
                          value={optionalFieldState[key]}
                        />
                      )
                    )}
                  </Fragment>
                )}
                <div className="grid__cell--1">
                  <div className="grid form-controls">
                    <button
                      className="btn grid__cell--1/2 grid__cell--1/4@widescreen"
                      data-icon="clear"
                      data-quid="store-locator-clear"
                      onClick={handleClear}
                      type="button"
                    >
                      {t("shared:action.clear_form")}
                    </button>
                    {isDefaultLocatorStrategy && (
                      <button
                        className="btn grid__cell--1/2 grid__cell--1/4@widescreen"
                        data-icon="pin_drop"
                        data-quid="store-locator-search"
                        onClick={handleSearch}
                        type="button"
                      >
                        {t("shared:action.drop_pin")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    case SERVICE_METHOD.CARRYOUT:
    default:
      return (
        <GoogleMapsTypeAhead
          className="grid__cell--1"
          input={address}
          label={t(ADDRESS_FIELDS[ADDRESS.ADDRESS].label)}
          maps={maps}
          name="address"
          onChange={({ address, placeId }) =>
            handleChange({
              address,
              placeId,
            })
          }
          options={{
            componentRestrictions: {
              country: countryCodeList,
            },
            types: ["geocode"],
          }}
          quidBase="store-locator-address"
        />
      );
  }
};

ES.propTypes = {
  address: PropTypes.string,
  addressLine2: PropTypes.string,
  city: PropTypes.string,
  cities: PropTypes.objectOf(PropTypes.string),
  getCitiesWithTypeahead: PropTypes.func,
  getRegions: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  getSearchCep: PropTypes.func.isRequired,
  getStreets: PropTypes.func.isRequired,
  postalCode: PropTypes.string,
  latLngAvailability: PropTypes.bool.isRequired,
  maps: PropTypes.objectOf(PropTypes.any).isRequired,
  neighborhood: PropTypes.string,
  locatorStrategy: PropTypes.string.isRequired,
  onError: PropTypes.func,
  placeId: PropTypes.string,
  regions: PropTypes.object,
  regionCode: PropTypes.string,
  searchCep: PropTypes.arrayOf(PropTypes.object).isRequired,
  serviceMethod: PropTypes.oneOf(SERVICE_METHOD_SORT),
  setLatLngAvailability: PropTypes.func.isRequired,
  setUsesPlaces: PropTypes.func.isRequired,
  setShowForm: PropTypes.func.isRequired,
  showForm: PropTypes.bool,
  setLocatorStrategy: PropTypes.func.isRequired,
  streets: PropTypes.string,
  streetName: PropTypes.string,
  streetNumber: PropTypes.string,
};

ES.defaultProps = {
  address: "",
  addressType: ADDRESS.LANDED,
  onError: (error) => {
    //eslint-disable-next-line no-console
    window.ENVCONFIG.logErrors && console.error(error);
  },
  getSearchCep: () => {},
  serviceMethod: SERVICE_METHOD.CARRYOUT,
};

export default ES;
